import { ref, computed, onMounted, onUnmounted } from 'vue';
import cashRegisterService from '@/services/cashRegisterService';
import type { Transaction, CashRegisterSession } from '@/utils/cashRegisterHelpers';
import { 
  calculateCurrentBalance,
  calculateRealCashBalance, 
  calculateSessionExpenses, 
  calculateSessionDuration,
  calculateSessionSales,
  calculateSalesCount,
  calculateExpensesCount
} from '@/utils/cashRegisterHelpers';

/**
 * Composable for cash register session management
 * Implements Single Responsibility Principle - only handles session and transaction state
 */
export function useCashRegisterSession() {
  // State
  const currentSession = ref<CashRegisterSession | null>(null);
  const transactions = ref<Transaction[]>([]);
  const isRefreshing = ref(false);
  const currentTime = ref(new Date());
  const lastCut = ref<any>(null);
  const lastCutLoading = ref(false);

  // Computed properties
  const currentBalance = computed(() => {
    if (!currentSession.value) return 0;
    return calculateCurrentBalance(currentSession.value.initial_balance || 0, transactions.value);
  });

  const realCashBalance = computed(() => {
    if (!currentSession.value) return 0;
    return calculateRealCashBalance(currentSession.value.initial_balance || 0, transactions.value);
  });

  const sessionExpenses = computed(() => {
    return calculateSessionExpenses(transactions.value);
  });

  const sessionSales = computed(() => {
    return calculateSessionSales(transactions.value);
  });

  const salesCount = computed(() => {
    return calculateSalesCount(transactions.value);
  });

  const expensesCount = computed(() => {
    return calculateExpensesCount(transactions.value);
  });

  const sessionDuration = computed(() => {
    if (!currentSession.value?.opened_at) return '0h 0m';
    return calculateSessionDuration(currentSession.value.opened_at, currentTime.value);
  });

  /**
   * Load current active session
   */
  const loadCurrentSession = async () => {
    try {
      isRefreshing.value = true;
      const response = await cashRegisterService.getCurrentSession();
      currentSession.value = (response as any) || null;
      if (currentSession.value) {
        await loadTransactions();
        await loadLastCut();
      }
    } catch (error) {
      console.error('Error loading current session:', error);
    } finally {
      isRefreshing.value = false;
    }
  };

  /**
   * Load transactions for current session
   */
  const loadTransactions = async () => {
    if (!currentSession.value) return;

    try {
      const transactionsResponse = await cashRegisterService.getTransactions(currentSession.value.id);
      const transactionsData = Array.isArray(transactionsResponse) 
        ? transactionsResponse 
        : transactionsResponse.data || [];
      transactions.value = transactionsData;
    } catch (error) {
      console.error('Error loading transactions:', error);
      transactions.value = [];
    }
  };

  /**
   * Load last cut information
   */
  const loadLastCut = async () => {
    if (!currentSession.value) {
      lastCut.value = null;
      return;
    }

    try {
      lastCutLoading.value = true;
      const cutData = await cashRegisterService.getLastCut(currentSession.value.id);
      lastCut.value = cutData || null;
    } catch (error) {
      console.error('Error loading last cut:', error);
      lastCut.value = null;
    } finally {
      lastCutLoading.value = false;
    }
  };

  /**
   * Open a new session
   */
  const openSession = async (initialBalance: number): Promise<void> => {
    const response = await cashRegisterService.openSession(initialBalance);
    currentSession.value = response as any;
    await loadTransactions();
    await loadCurrentSession();
  };

  /**
   * Close current session
   */
  const closeSession = async (
    actualBalance: number, 
    notes?: string, 
    denominations?: any
  ): Promise<void> => {
    if (!currentSession.value) return;

    try {
      // Generate automatic cut before closing session
      console.log('Generating automatic cut before closing session...');
      
      // Calculate payment breakdown from transactions
      const paymentBreakdown: Record<string, number> = {
        cash: transactions.value
          .filter(t => t.payment_method === 'cash' && t.transaction_type === 'sale')
          .reduce((sum, t) => sum + (t.amount || 0), 0),
        card: transactions.value
          .filter(t => t.payment_method === 'card' && t.transaction_type === 'sale')
          .reduce((sum, t) => sum + (t.amount || 0), 0),
        digital: transactions.value
          .filter(t => t.payment_method === 'digital' && t.transaction_type === 'sale')
          .reduce((sum, t) => sum + (t.amount || 0), 0),
        other: transactions.value
          .filter(t => t.payment_method === 'other' && t.transaction_type === 'sale')
          .reduce((sum, t) => sum + (t.amount || 0), 0)
      };
      
      // Add custom payment methods if configured
      // Import dynamically to avoid circular dependencies
      const { useAuthStore } = await import('@/stores/auth');
      const authStore = useAuthStore();
      const customMethods = (authStore.restaurant as any)?.payment_methods_config?.custom_methods || [];
      
      customMethods.forEach((method: any) => {
        if (method.enabled) {
          paymentBreakdown[method.name] = transactions.value
            .filter(t => t.payment_method === method.name && t.transaction_type === 'sale')
            .reduce((sum, t) => sum + (t.amount || 0), 0);
        }
      });
      
      await cashRegisterService.cutSession(currentSession.value.id, paymentBreakdown);
      console.log('Automatic cut created successfully before closing session');
    } catch (cutError) {
      console.warn('Failed to create automatic cut before closing session:', cutError);
      // Continue with session close even if cut fails
    }

    if (denominations) {
      await cashRegisterService.closeSessionWithDenominations(
        currentSession.value.id,
        actualBalance,
        notes,
        denominations
      );
    } else {
      await cashRegisterService.closeSession(
        currentSession.value.id,
        actualBalance,
        notes
      );
    }
    currentSession.value = null;
    transactions.value = [];
    await loadCurrentSession();
  };

  /**
   * Add expense to current session
   */
  const addExpense = async (amount: number, description: string, category?: string): Promise<void> => {
    if (!currentSession.value) {
      throw new Error('No active session');
    }

    await cashRegisterService.addExpense(currentSession.value.id, {
      amount,
      description,
      category
    });

    await loadCurrentSession();
  };

  /**
   * Perform a cut
   */
  const performCut = async (paymentBreakdown: {
    cash: number;
    card: number;
    digital: number;
    other: number;
  }): Promise<any> => {
    if (!currentSession.value) {
      throw new Error('No active session');
    }

    const result = await cashRegisterService.cutSession(currentSession.value.id, paymentBreakdown);
    await loadCurrentSession();
    return result.data || result;
  };

  /**
   * Delete a transaction
   */
  const deleteTransaction = async (transactionId: number): Promise<void> => {
    await cashRegisterService.deleteTransaction(transactionId);
    await loadCurrentSession();
  };

  /**
   * Set up timer for session duration updates
   */
  const setupTimer = () => {
    const timerInterval = setInterval(() => {
      currentTime.value = new Date();
    }, 60000); // Update every minute

    return timerInterval;
  };

  /**
   * Set up event listener for order payment completion
   */
  const setupEventListeners = () => {
    const handlePaymentCompleted = () => {
      loadCurrentSession();
    };

    window.addEventListener('orderPaymentCompleted', handlePaymentCompleted);

    return () => {
      window.removeEventListener('orderPaymentCompleted', handlePaymentCompleted);
    };
  };

  // Lifecycle
  let timerInterval: ReturnType<typeof setInterval> | null = null;
  let cleanupEventListeners: (() => void) | null = null;

  onMounted(() => {
    loadCurrentSession();
    timerInterval = setupTimer();
    cleanupEventListeners = setupEventListeners();
  });

  onUnmounted(() => {
    if (timerInterval) {
      clearInterval(timerInterval);
    }
    if (cleanupEventListeners) {
      cleanupEventListeners();
    }
  });

  return {
    // State
    currentSession,
    transactions,
    isRefreshing,
    lastCut,
    lastCutLoading,

    // Computed
    currentBalance,
    realCashBalance,
    sessionExpenses,
    sessionSales,
    salesCount,
    expensesCount,
    sessionDuration,

    // Methods
    loadCurrentSession,
    loadTransactions,
    openSession,
    closeSession,
    addExpense,
    performCut,
    deleteTransaction
  };
}
